import gql from "graphql-tag";
import type { ApolloClient } from "@apollo/client";

import type {
  GetAllDocumentsQuery,
  GetAllDocumentsQueryVariables,
  GetEmployerDocumentsQuery,
  GetEmployerDocumentsQueryVariables,
  GetDocumentDownloadUrlQuery,
  GetDocumentDownloadUrlQueryVariables,
  GetDocUrlQuery,
  GetDocUrlQueryVariables,
  GetEmployerDocumentDownloadUrlQuery,
  GetEmployerDocumentDownloadUrlQueryVariables,
  GetEmployerTakeRateDocumentDownloadUrlQuery,
  GetEmployerTakeRateDocumentDownloadUrlQueryVariables,
} from "types/graphql";

export function getDocuments(
  client: ApolloClient<object>,
  accountId: string,
  provider?: string
): Promise<GetAllDocumentsQuery["getAllDocuments"]> {
  return new Promise((resolve, reject) => {
    const DOCUMENTS = gql`
      query getAllDocuments($accountId: String!, $provider: String) {
        getAllDocuments(accountId: $accountId, provider: $provider) {
          name
          id
          correlationId
          uri
          contentType
          createdAt
          updatedAt
          type
          provider
        }
      }
    `;
    const result = client.query<
      GetAllDocumentsQuery,
      GetAllDocumentsQueryVariables
    >({
      query: DOCUMENTS,
      variables: { accountId, provider },
    });
    result.then(
      (data) => {
        resolve(data.data.getAllDocuments);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

export function getEmployerDocuments(
  client: ApolloClient<object>,
  provider?: string,
  companyId?: string,
  type?: string
): Promise<GetEmployerDocumentsQuery["getEmployerDocuments"]> {
  return new Promise((resolve, reject) => {
    const EMPLOYER_DOCUMENTS = gql`
      query getEmployerDocuments(
        $companyId: ID
        $provider: String
        $type: String
      ) {
        getEmployerDocuments(
          companyId: $companyId
          provider: $provider
          type: $type
        ) {
          name
          id
          uri
          contentType
          createdAt
          updatedAt
          type
          provider
        }
      }
    `;
    const result = client.query<
      GetEmployerDocumentsQuery,
      GetEmployerDocumentsQueryVariables
    >({
      query: EMPLOYER_DOCUMENTS,
      variables: { companyId, provider, type },
    });
    result.then(
      (data) => {
        resolve(data.data.getEmployerDocuments);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

export function getUrlForDocument(
  client: ApolloClient<object>,
  accountId: string,
  documentId: string
): Promise<GetDocUrlQuery["getDocUrl"]> {
  return new Promise((resolve, reject) => {
    const DOCUMENT_URL = gql`
      query getDocUrl($accountId: String!, $documentId: String!) {
        getDocUrl(accountId: $accountId, documentId: $documentId) {
          validUntil
          url
        }
      }
    `;
    const result = client.query<GetDocUrlQuery, GetDocUrlQueryVariables>({
      query: DOCUMENT_URL,
      variables: { accountId, documentId },
    });
    result.then(
      (data) => {
        resolve(data.data.getDocUrl);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

export function getDownloadUrl(
  client: ApolloClient<object>,
  userId: string,
  documentId: string
): Promise<GetDocumentDownloadUrlQuery["getDocumentDownloadUrl"]> {
  return new Promise((resolve, reject) => {
    const DOWNLOAD_URL = gql`
      query getDocumentDownloadUrl($userId: ID!, $documentId: ID!) {
        getDocumentDownloadUrl(userId: $userId, documentId: $documentId) {
          validUntil
          url
        }
      }
    `;
    const result = client.query<
      GetDocumentDownloadUrlQuery,
      GetDocumentDownloadUrlQueryVariables
    >({
      query: DOWNLOAD_URL,
      variables: { userId, documentId },
      errorPolicy: "none",
    });
    result.then(
      (data) => {
        resolve(data.data.getDocumentDownloadUrl);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function getEmployerDownloadUrl(
  client: ApolloClient<object>,
  { documentId, companyId }: GetEmployerDocumentDownloadUrlQueryVariables
): Promise<
  GetEmployerDocumentDownloadUrlQuery["getEmployerDocumentDownloadUrl"]
> {
  return new Promise((resolve, reject) => {
    const URL = gql`
      query getEmployerDocumentDownloadUrl($documentId: ID!, $companyId: ID) {
        getEmployerDocumentDownloadUrl(
          documentId: $documentId
          companyId: $companyId
        ) {
          validUntil
          url
        }
      }
    `;
    const result = client.query<
      GetEmployerDocumentDownloadUrlQuery,
      GetEmployerDocumentDownloadUrlQueryVariables
    >({
      query: URL,
      variables: { documentId, companyId },
      errorPolicy: "none",
    });
    result.then(
      (data) => {
        resolve(data.data.getEmployerDocumentDownloadUrl);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function getEmployerTakeRateDownloadUrl(
  client: ApolloClient<object>,
  {
    groupId,
    status,
    timeframe,
    companyId,
  }: GetEmployerTakeRateDocumentDownloadUrlQueryVariables
): Promise<
  GetEmployerTakeRateDocumentDownloadUrlQuery["getEmployerTakeRateDocumentDownloadUrl"]
> {
  return new Promise((resolve, reject) => {
    const URL = gql`
      query getEmployerTakeRateDocumentDownloadUrl(
        $groupId: ID
        $status: String!
        $timeframe: String!
        $companyId: ID
      ) {
        getEmployerTakeRateDocumentDownloadUrl(
          groupId: $groupId
          status: $status
          timeframe: $timeframe
          companyId: $companyId
        ) {
          validUntil
          url
        }
      }
    `;
    const result = client.query<
      GetEmployerTakeRateDocumentDownloadUrlQuery,
      GetEmployerTakeRateDocumentDownloadUrlQueryVariables
    >({
      query: URL,
      variables: { groupId, status, timeframe, companyId },
      errorPolicy: "none",
    });
    result.then(
      (data) => {
        resolve(data.data.getEmployerTakeRateDocumentDownloadUrl);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export default {
  getDocuments,
  getUrlForDocument,
  getDownloadUrl,
  getEmployerTakeRateDownloadUrl,
};
